<template>
  <BasePopup
    ref="popup"
    class="hesitation-popup"
    :class="{ 'hesitation-popup--subscribing': subscribing }"
    :compact="true"
    :title="title"
    @hide="emitter.emit('hesitationPopupClose')"
  >
    <template v-if="visible && annualPlan">
      <p class="hesitation-popup__subtitle">
        Get this exclusive discount if you start your Annual Trial today.
      </p>

      <div class="hesitation-popup__card">
        <div class="hesitation-popup__badge">SPECIAL OFFER</div>

        <div class="hesitation-popup__price-row">
          <div>
            <div class="hesitation-popup__plan-name-group">
              <div class="hesitation-popup__plan-name">Annual</div>
              <TrialBadge
                v-if="!billing.isTrialOver"
                class="hesitation-popup__trial-badge"
                :days="trialLength"
                compact
              />
            </div>
            <p class="hesitation-popup__price-sub">
              Billed at
              <span class="wg-no-translate">{{ billedPerPeriod }}</span>
              (normally
              <span class="wg-no-translate">{{ oldBilledPerPeriod }}</span
              >)
            </p>
          </div>
          <div class="hesitation-popup__price">
            <div class="wg-no-translate">{{ displayPrice }}</div>
            <div class="hesitation-popup__price-old wg-no-translate">
              {{ displayOldPrice }}
            </div>
          </div>
        </div>

        <div class="hesitation-popup__cta" @click="handleSubscribeClick">
          <BillingSubscribeButton
            :billing="billing"
            :plan="annualPlan"
            location="hesitation_popup"
            :label="ctaLabel"
          />
        </div>

        <p class="hesitation-popup__cancel-line">
          Cancel anytime before day {{ trialLength }}, no charge.
        </p>
      </div>
    </template>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { Raw } from 'vue'
import type { BillingView, PlanView } from '@/models/billing'
import type { PlanPrice } from '@/models/billing.plans'
import { AnnualPlanView } from '@/models/billing.plans'
import BasePopup from '@/components/ui/BasePopup.vue'
import BillingSubscribeButton from './BillingSubscribeButton.vue'
import TrialBadge from './TrialBadge.vue'
import { backend } from '@/services/backend'
import { emitter } from '@/services/mitt'
import { Sentry } from '@/services/sentry'

const props = defineProps<{
  billing: BillingView
}>()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)
const visible = ref(false)
const subscribing = ref(false)
const annualPlan = ref<Raw<PlanView> | null>(null)

function getPrice(price: PlanPrice): string {
  if (props.billing.platform === 'android') {
    return price.withPeriod.perYear
  }
  return price.withPeriod.perMonth
}

const displayPrice = computed(() => {
  return annualPlan.value ? getPrice(annualPlan.value.price) : null
})

// The popup only shows once the hesitation discount has been applied, so
// annualPlan always has a discount by the time this is rendered.
const displayOldPrice = computed(() => {
  return annualPlan.value ? getPrice(annualPlan.value.priceNoDiscount) : null
})

// The hesitation discount is always a percentage-type coupon.
const discountPercent = computed(
  () => annualPlan.value?.discount?.value ?? null,
)

const title = computed(() => {
  return `Take an extra ${discountPercent.value}% off your first year.`
})

const ctaLabel = computed(() => {
  return `Claim ${discountPercent.value}% Discount`
})

const trialLength = computed(() => props.billing.trialLength('annual'))

const billedPerPeriod = computed(() => {
  if (!annualPlan.value) {
    return ''
  }
  return `${annualPlan.value.formatPrice(
    annualPlan.value.normalizedAmount.perPeriod,
  )}/yr`
})

const oldBilledPerPeriod = computed(() => {
  if (!annualPlan.value) {
    return null
  }
  return annualPlan.value.formatPrice(
    annualPlan.value.normalizedAmountNoDiscount.perPeriod,
  )
})

async function handleOpen(): Promise<void> {
  if (visible.value) {
    return
  }

  try {
    const response = await backend.applyHesitationDiscount()
    props.billing.update(response)
    annualPlan.value = AnnualPlanView.createView(response.billing_plans)
    visible.value = true
    popup.value?.show()
  } catch (error) {
    Sentry.captureException(error)
  }
}

function handleSubscribeClick(): void {
  // The click bubbles from BillingSubscribeButton, which opens its own
  // SubscribePopup nested inside this popup's slot. We can't call
  // popup.hide() here - BasePopup unmounts its whole slot on hide, which
  // would tear down the just-opened SubscribePopup along with it. Instead,
  // hide this popup visually only (see the --subscribing CSS below), which
  // keeps it mounted so the nested SubscribePopup survives.
  subscribing.value = true
}

onMounted(() => {
  emitter.on('hesitationPopupOpen', handleOpen)
})

onUnmounted(() => {
  emitter.off('hesitationPopupOpen', handleOpen)
})
</script>

<style scoped lang="scss">
.hesitation-popup {
  &.popup--compact {
    --popup-width: 634px;
  }

  &.hesitation-popup--subscribing {
    visibility: hidden;
    pointer-events: none;

    // The nested SubscribePopup (opened from this popup's CTA) must stay
    // visible - visibility is inherited, so this overrides it back.
    :deep(.subscribe-popup) {
      visibility: visible;
      pointer-events: auto;
    }
  }

  :deep(.popup__title) {
    margin-top: 32px;
    text-align: center;

    @media (max-width: $w-medium-s) {
      margin-top: 0;
    }
  }

  :deep(.popup__close) {
    position: absolute;
    top: 20px;
    right: 20px;
  }

  :deep(.popup__inner) {
    padding-top: 8px;
    padding-bottom: 16px;
  }

  &__subtitle {
    text-align: center;
    color: $c-black-6;
    margin: -8px 0 20px;
    padding-bottom: 16px;
  }

  &__card {
    position: relative;
    border: 2px solid $c-yellow-4;
    border-radius: 12px;
    padding: 20px 20px 22px;
  }

  &__badge {
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    background: $c-blue-4;
    color: $c-white;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 999px;
    white-space: nowrap;
  }

  &__price-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 16px;
    gap: 12px;
    padding-top: 20px;
  }

  &__plan-name-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__plan-name {
    font-size: 24px;
    font-weight: 700;
    color: $c-black-15;
  }

  &__trial-badge {
    color: $c-black-3;
    background-color: $c-bg;
    margin-top: 0;
  }

  &__price-sub {
    font-size: 13px;
    color: $c-black-6;
    margin: 4px 0 0;
  }

  &__price {
    text-align: right;
    font-size: 24px;
    font-weight: 700;
  }

  &__price-old {
    font-size: 13px;
    font-weight: 400;
    color: $c-black-6;
    text-decoration: line-through;
    margin-top: 4px;
  }

  &__cta {
    margin-top: 22px;

    :deep(.subscribe-button) {
      width: 100%;
    }
  }

  &__cancel-line {
    margin: 13px 0 0;
    text-align: center;
    font-size: 13px;
    color: $c-black-6;
  }
}

.night-mode {
  .hesitation-popup {
    &__plan-name {
      color: $c-white;
    }
  }
}
</style>
